// Todos — page-tree boards with multi-view (board / table / calendar),
// search, filter panel, and member tagging. Sidebar lives in <TodosSidebar/>.
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { I } from "../../shared/Icon";
import { toast } from "../../shared/stores/toasts";
import { useAuth } from "../../shared/stores/auth";
import { CardDetail } from "./CardDetail";
import { OrgPicker } from "../../shared/org/OrgPicker";

// ── Types ─────────────────────────────────────────────────────────────────────

type ColStatus = "inbox" | "today" | "doing" | "wait" | "hold" | "delay" | "done" | "cancel";
type ViewMode = "board" | "table" | "calendar";

type LabelInfo = { id: number; name: string; color: string };
type PageInfo = { id: number; title: string; icon: string; icon_color: string | null; parent_id: number | null; sort_order: number };

type Todo = {
  id: number;
  user_id: number;
  title: string;
  note: string | null;
  is_done: boolean;
  start_date: string | null;
  due_date: string | null;
  start_time: string | null;
  due_time: string | null;
  priority: string;
  status: string;
  category: string;
  scope: string;
  page_id: number | null;
  cover_color: string | null;
  is_archived: boolean;
  assignee_ids: number[];
  label_ids: number[];
  checklist_summary: { total: number; done: number };
  created_at: string;
};

type DirUser = {
  id: number;
  name: string;
  username: string | null;
  email: string;
  department_name: string | null;
};

type CtxState = { x: number; y: number; todo: Todo | null; col: string };

type FilterState = {
  labels: number[];
  priorities: string[];
  due: "all" | "overdue" | "soon" | "none";
  mineOnly: boolean;
};
const EMPTY_FILTER: FilterState = { labels: [], priorities: [], due: "all", mineOnly: false };

// ── Constants ─────────────────────────────────────────────────────────────────

const COLS: { id: ColStatus; label: string; color: string; hint: string }[] = [
  { id: "inbox",  label: "수신함",    color: "#94A3B8", hint: "새로 들어온 할 일" },
  { id: "today",  label: "오늘 할 일", color: "#3B82F6", hint: "오늘 처리할 항목" },
  { id: "doing",  label: "진행 중",   color: "#F97316", hint: "지금 작업 중" },
  { id: "wait",   label: "대기 중",   color: "#8B5CF6", hint: "외부 의존 대기" },
  { id: "hold",   label: "보류",      color: "#EAB308", hint: "잠시 멈춤(보류)" },
  { id: "delay",  label: "지연",      color: "#EF4444", hint: "일정 지연" },
  { id: "done",   label: "완료",      color: "#22C55E", hint: "처리 완료" },
  { id: "cancel", label: "취소",      color: "#6B7280", hint: "취소됨 — 자동 보관되어 목록에서 숨겨집니다" },
];

const PRIORITY_COLOR: Record<string, string> = { URGENT: "#EF4444", HIGH: "#F97316", NORMAL: "#3B82F6", LOW: "#94A3B8" };
const PRIORITY_LABEL: Record<string, string> = { URGENT: "긴급", HIGH: "높음", NORMAL: "보통", LOW: "낮음" };
const PRIORITY_OPTIONS = [
  { value: "URGENT", label: "긴급", color: "#EF4444" },
  { value: "HIGH",   label: "높음", color: "#F97316" },
  { value: "NORMAL", label: "보통", color: "#3B82F6" },
  { value: "LOW",    label: "낮음", color: "#94A3B8" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function mapStatus(todo: Todo): ColStatus {
  if (todo.is_done) return "done";
  const s = todo.status as ColStatus;
  return COLS.find(c => c.id === s) ? s : "inbox";
}
function avatarColor(id: number) {
  const p = ["#6366f1","#0891b2","#d97706","#16a34a","#dc2626","#7c3aed","#db2777","#0d9488"];
  return p[id % p.length];
}
function userInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
}
function daysUntil(due: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(`${due}T00:00:00`);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

// ── Avatar ────────────────────────────────────────────────────────────────────

function Avatar({ userId, users, size = 20 }: { userId: number; users: DirUser[]; size?: number }) {
  const u = users.find(x => x.id === userId);
  return (
    <div title={u?.name ?? `ID ${userId}`} style={{
      width: size, height: size, borderRadius: "50%", background: avatarColor(userId),
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.4, fontWeight: 700, color: "#fff",
      flexShrink: 0, border: "1.5px solid var(--bg-0, #fff)",
    }}>
      {u ? userInitials(u.name) : "?"}
    </div>
  );
}

function AvatarStack({ ids, users, size = 18 }: { ids: number[]; users: DirUser[]; size?: number }) {
  if (!ids.length) return null;
  return (
    <div style={{ display: "flex" }}>
      {ids.slice(0, 4).map(uid => <Avatar key={uid} userId={uid} users={users} size={size} />)}
      {ids.length > 4 && (
        <div style={{ width: size, height: size, borderRadius: "50%", background: "var(--bg-2, #e5e7eb)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: size * 0.45, fontWeight: 700, color: "var(--text-3)", border: "1.5px solid var(--bg-0)" }}>
          +{ids.length - 4}
        </div>
      )}
    </div>
  );
}

// ── Context Menu ──────────────────────────────────────────────────────────────

type CMenuItem = { label?: string; icon?: string; onClick?: () => void | Promise<void>; danger?: boolean; sep?: boolean; disabled?: boolean; children?: CMenuItem[] };

const cmBtn = (it: CMenuItem): React.CSSProperties => ({
  display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "6px 10px", border: "none",
  background: "transparent", cursor: it.disabled ? "default" : "pointer",
  color: it.danger ? "#ef4444" : it.disabled ? "var(--text-3)" : "var(--text-1)", borderRadius: 4, textAlign: "left", fontSize: 13,
});

function MenuRow({ it, onClose, flipLeft }: { it: CMenuItem; onClose: () => void; flipLeft: boolean }) {
  const [openSub, setOpenSub] = useState(false);
  if (it.sep) return <div style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />;
  if (it.children) {
    return (
      <div style={{ position: "relative" }} onMouseEnter={() => setOpenSub(true)} onMouseLeave={() => setOpenSub(false)}>
        <button style={cmBtn(it)} onMouseEnter={e => (e.currentTarget.style.background = "var(--hover, #f3f4f6)")} onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
          {it.icon && <span style={{ fontSize: 14, lineHeight: 1 }}>{it.icon}</span>}
          <span style={{ flex: 1 }}>{it.label}</span>
          <span style={{ color: "var(--text-3)", fontSize: 11 }}>▶</span>
        </button>
        {openSub && (
          <div style={{ position: "absolute", top: -6, [flipLeft ? "right" : "left"]: "100%", width: 220, maxHeight: 340, overflowY: "auto",
            background: "var(--bg-0,#fff)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 8px 28px rgba(0,0,0,0.18)", padding: 6, zIndex: 10000 } as React.CSSProperties}>
            {it.children.map((c, j) => c.sep ? <div key={j} style={{ height: 1, background: "var(--border)", margin: "4px 0" }} /> : (
              <button key={j} onClick={() => { if (!c.disabled) { c.onClick?.(); onClose(); } }} disabled={c.disabled} style={cmBtn(c)}
                onMouseEnter={e => { if (!c.disabled) e.currentTarget.style.background = "var(--hover, #f3f4f6)"; }}
                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                {c.icon && <span style={{ fontSize: 14, lineHeight: 1 }}>{c.icon}</span>}
                <span>{c.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <button onClick={() => { it.onClick?.(); onClose(); }} disabled={it.disabled} style={cmBtn(it)}
      onMouseEnter={e => { if (!it.disabled) e.currentTarget.style.background = "var(--hover, #f3f4f6)"; }}
      onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
      {it.icon && <span style={{ fontSize: 14, lineHeight: 1 }}>{it.icon}</span>}
      <span>{it.label}</span>
    </button>
  );
}

function ContextMenu({ x, y, items, onClose }: { x: number; y: number; items: CMenuItem[]; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [onClose]);
  const W = 210, H = items.length * 32 + 16;
  const left = Math.min(x, window.innerWidth - W - 8);
  const top = Math.min(y, window.innerHeight - H - 8);
  const flipLeft = left + W + 224 > window.innerWidth;  // 서브메뉴가 오른쪽으로 넘치면 왼쪽으로
  return (
    <div ref={ref} style={{ position: "fixed", left, top, width: W, zIndex: 9999, background: "var(--bg-0, #fff)",
      border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 8px 28px rgba(0,0,0,0.18)", padding: 6, fontSize: 13 }}
      onContextMenu={e => e.preventDefault()}>
      {items.map((it, i) => <MenuRow key={i} it={it} onClose={onClose} flipLeft={flipLeft} />)}
    </div>
  );
}

// ── Assignee Picker ───────────────────────────────────────────────────────────

function AssigneePicker({ users, selected, onChange }: { users: DirUser[]; selected: number[]; onChange: (ids: number[]) => void }) {
  const [q, setQ] = useState("");
  const filtered = users.filter(u => !q || u.name.includes(q) || (u.username ?? "").includes(q) || u.email.includes(q));
  const toggle = (id: number) => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  return (
    <div>
      <input className="t-input" placeholder="이름 · 아이디 검색..." value={q} onChange={e => setQ(e.target.value)} style={{ marginBottom: 6 }} />
      <div style={{ border: "1px solid var(--border)", borderRadius: 6, maxHeight: 180, overflowY: "auto" }}>
        {filtered.length === 0 && <div style={{ padding: "12px 14px", fontSize: 12, color: "var(--text-3)", textAlign: "center" }}>검색 결과 없음</div>}
        {filtered.map(u => (
          <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", cursor: "pointer",
            borderBottom: "1px solid var(--border)", background: selected.includes(u.id) ? "var(--p-50, #eef2ff)" : "transparent" }}>
            <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} style={{ accentColor: "var(--p-500, #6366f1)" }} />
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: avatarColor(u.id), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {userInitials(u.name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.department_name ?? u.email}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

// ── Edit / Create Modal ───────────────────────────────────────────────────────

type EditPayload = {
  id?: number;
  title: string;
  note: string;
  priority: string;
  start_date: string;
  due_date: string;
  start_time: string;
  due_time: string;
  status: ColStatus;
  category: string;
  assignee_ids: number[];
  page_id: number | null;
};

function EditModal({ initial, users, pages, categories, onClose, onSave }: {
  initial: EditPayload; users: DirUser[]; pages: PageInfo[]; categories: string[];
  onClose: () => void; onSave: (p: EditPayload) => Promise<void>;
}) {
  const [form, setForm] = useState<EditPayload>(initial);
  const [saving, setSaving] = useState(false);
  const [showOrg, setShowOrg] = useState(false);
  const isNew = !initial.id;
  const set = <K extends keyof EditPayload>(k: K, v: EditPayload[K]) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (e: any) { toast.error("저장 실패", e?.response?.data?.error?.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={e => e.stopPropagation()} style={{ width: 500, maxWidth: "95vw" }}>
        <div style={{ padding: "16px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 14, flex: 1 }}>{isNew ? "새 할 일" : "할 일 수정"}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "var(--text-3)", lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: 18, display: "grid", gap: 14 }}>
          <div>
            <label className="t-label">제목 *</label>
            <input className="t-input" autoFocus value={form.title} placeholder="할 일 제목..."
              onChange={e => set("title", e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) submit(); }} />
          </div>

          <div>
            <label className="t-label">메모</label>
            <textarea className="t-input" value={form.note} placeholder="상세 내용..."
              onChange={e => set("note", e.target.value)} style={{ minHeight: 68, resize: "vertical", fontFamily: "inherit" }} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="t-label">우선순위</label>
              <div style={{ display: "flex", gap: 4 }}>
                {PRIORITY_OPTIONS.map(p => (
                  <button key={p.value} onClick={() => set("priority", p.value)}
                    style={{ flex: 1, padding: "5px 4px", border: `1.5px solid ${form.priority === p.value ? p.color : "var(--border)"}`,
                      borderRadius: 6, background: form.priority === p.value ? `${p.color}18` : "transparent",
                      color: form.priority === p.value ? p.color : "var(--text-3)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="t-label">기간 (시작 ~ 마감)</label>
              <div style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 4 }}>
                <input className="t-input" type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)} style={{ flex: 1 }} />
                <input className="t-input" type="time" value={form.start_time} onChange={e => set("start_time", e.target.value)} style={{ width: 92 }} />
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <input className="t-input" type="date" value={form.due_date} onChange={e => set("due_date", e.target.value)} style={{ flex: 1 }} />
                <input className="t-input" type="time" value={form.due_time} onChange={e => set("due_time", e.target.value)} style={{ width: 92 }} />
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label className="t-label">진행상황</label>
              <select className="t-input" value={form.status} onChange={e => set("status", e.target.value as ColStatus)}>
                {COLS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="t-label">카테고리</label>
              <input className="t-input" list="todo-category-options" value={form.category} placeholder="예: 기획 / 개발 …"
                onChange={e => set("category", e.target.value)} />
              <datalist id="todo-category-options">
                {categories.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          <div>
            <label className="t-label">페이지(폴더)</label>
            <select className="t-input" value={form.page_id ?? ""} onChange={e => set("page_id", e.target.value ? Number(e.target.value) : null)}>
              <option value="">전체 (페이지 없음)</option>
              {pages.map(p => <option key={p.id} value={p.id}>{p.parent_id ? "  └ " : ""}{p.title}</option>)}
            </select>
          </div>

          <div>
            <label className="t-label">담당자 초대</label>
            {form.assignee_ids.length > 0 && (
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
                {form.assignee_ids.map(uid => {
                  const u = users.find(x => x.id === uid);
                  return (
                    <span key={uid} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px 2px 4px", borderRadius: 12,
                      background: `${avatarColor(uid)}18`, border: `1px solid ${avatarColor(uid)}40`, fontSize: 12, color: avatarColor(uid), fontWeight: 500 }}>
                      <span style={{ width: 16, height: 16, borderRadius: "50%", background: avatarColor(uid), color: "#fff", fontSize: 9, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                        {u ? userInitials(u.name) : uid}
                      </span>
                      {u?.name ?? `ID ${uid}`}
                      <button onClick={() => set("assignee_ids", form.assignee_ids.filter(x => x !== uid))} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1, color: "currentColor", opacity: 0.6 }}>✕</button>
                    </span>
                  );
                })}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}><AssigneePicker users={users} selected={form.assignee_ids} onChange={ids => set("assignee_ids", ids)} /></div>
              <button type="button" className="t-btn" onClick={() => setShowOrg(true)} style={{ whiteSpace: "nowrap" }}>🏢 조직도</button>
            </div>
          </div>
        </div>

        {showOrg && (
          <OrgPicker multi excludeIds={form.assignee_ids} title="담당자 — 조직도에서 선택"
            onClose={() => setShowOrg(false)}
            onPick={(picked) => { set("assignee_ids", [...new Set([...form.assignee_ids, ...picked.map(p => p.id)])]); setShowOrg(false); }} />
        )}

        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose} disabled={saving}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={saving || !form.title.trim()}>
            {saving ? "저장 중..." : isNew ? "추가" : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Filter Panel ──────────────────────────────────────────────────────────────

function FilterPanel({ filters, allLabels, onChange, onClose }: {
  filters: FilterState; allLabels: LabelInfo[]; onChange: (f: FilterState) => void; onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [onClose]);

  const togglePriority = (v: string) =>
    onChange({ ...filters, priorities: filters.priorities.includes(v) ? filters.priorities.filter(x => x !== v) : [...filters.priorities, v] });
  const toggleLabel = (id: number) =>
    onChange({ ...filters, labels: filters.labels.includes(id) ? filters.labels.filter(x => x !== id) : [...filters.labels, id] });

  const dueOpts: { v: FilterState["due"]; label: string }[] = [
    { v: "all", label: "전체" }, { v: "overdue", label: "기한 초과" }, { v: "soon", label: "7일 이내" }, { v: "none", label: "마감일 없음" },
  ];

  return (
    <div ref={ref} style={{ position: "absolute", right: 0, top: 44, width: 280, zIndex: 200, background: "var(--bg-0)",
      border: "1px solid var(--border)", borderRadius: 12, boxShadow: "0 12px 32px rgba(0,0,0,0.18)", overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderBottom: "1px solid var(--border)" }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text-1)" }}>필터</span>
        <button onClick={() => onChange(EMPTY_FILTER)} style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", border: "none", background: "none", cursor: "pointer" }}>초기화</button>
      </div>
      <div style={{ padding: 14, display: "grid", gap: 16, maxHeight: "65vh", overflowY: "auto" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-1)", cursor: "pointer" }}>
          <input type="checkbox" checked={filters.mineOnly} onChange={e => onChange({ ...filters, mineOnly: e.target.checked })} style={{ accentColor: "var(--p-500)" }} />
          내 담당만 보기
        </label>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 }}>우선순위</div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {PRIORITY_OPTIONS.map(p => {
              const on = filters.priorities.includes(p.value);
              return (
                <button key={p.value} onClick={() => togglePriority(p.value)}
                  style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer",
                    border: `1.5px solid ${on ? p.color : "var(--border)"}`, background: on ? `${p.color}18` : "transparent", color: on ? p.color : "var(--text-3)" }}>
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 }}>마감일</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {dueOpts.map(o => (
              <button key={o.v} onClick={() => onChange({ ...filters, due: o.v })}
                style={{ padding: "5px 8px", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  border: `1.5px solid ${filters.due === o.v ? "var(--p-400)" : "var(--border)"}`,
                  background: filters.due === o.v ? "var(--p-50, #eef2ff)" : "transparent", color: filters.due === o.v ? "var(--p-600)" : "var(--text-3)" }}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {allLabels.length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 }}>라벨</div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {allLabels.map(lb => {
                const on = filters.labels.includes(lb.id);
                return (
                  <button key={lb.id} onClick={() => toggleLabel(lb.id)}
                    style={{ padding: "3px 9px", borderRadius: 10, fontSize: 11, fontWeight: 600, cursor: "pointer",
                      border: `1.5px solid ${on ? lb.color : "var(--border)"}`, background: on ? `${lb.color}22` : "transparent",
                      color: on ? lb.color : "var(--text-3)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: lb.color, display: "inline-block" }} />
                    {lb.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Todo Card (board) ─────────────────────────────────────────────────────────

function TodoCard({ todo, users, allLabels, currentUserId, onToggle, onCtx, onOpen, onRename, dragging, selected, onDragStart, onDragEnd }: {
  todo: Todo; users: DirUser[]; allLabels: LabelInfo[]; currentUserId: number;
  onToggle: () => void; onCtx: (e: React.MouseEvent) => void; onOpen: () => void; onRename: (title: string) => void;
  dragging: boolean; selected?: boolean; onDragStart: () => void; onDragEnd: () => void;
}) {
  const pColor = PRIORITY_COLOR[todo.priority.toUpperCase()] ?? "#94A3B8";
  const sc = mapStatus(todo);
  const scol = COLS.find(c => c.id === sc) ?? COLS[0];
  const isOwner = todo.user_id === currentUserId;
  const cardLabels = allLabels.filter(l => todo.label_ids?.includes(l.id));
  const cl = todo.checklist_summary;
  const hasCl = cl && cl.total > 0;
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(todo.title);

  return (
    <div draggable data-card-id={todo.id} onDragStart={(e) => { try { e.dataTransfer.setData("application/x-todo-id", String(todo.id)); e.dataTransfer.effectAllowed = "move"; } catch (_) {} onDragStart(); }} onDragEnd={onDragEnd} onContextMenu={onCtx} className="t-card"
      style={{ padding: 0, borderLeft: `4px solid ${scol.color}`, opacity: dragging ? 0.35 : 1, cursor: "grab", userSelect: "none", position: "relative", overflow: "hidden",
        background: selected ? "var(--p-50,#eef2ff)" : "linear-gradient(180deg, var(--bg-0,#fff), var(--bg-1,#fafafb))",
        outline: selected ? "2px solid var(--p-500,#6366f1)" : "none", outlineOffset: -1,
        boxShadow: "0 1px 2px rgba(15,23,42,0.06), 0 1px 1px rgba(15,23,42,0.04)" }}>
      {todo.cover_color && <div style={{ height: 28, background: todo.cover_color }} />}
      <div style={{ padding: "10px 12px" }}>
        {cardLabels.length > 0 && (
          <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 5 }}>
            {cardLabels.map(lb => <span key={lb.id} style={{ height: 8, width: 36, borderRadius: 4, background: lb.color, display: "inline-block" }} title={lb.name} />)}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <button onClick={e => { e.stopPropagation(); onToggle(); }}
            style={{ width: 16, height: 16, marginTop: 2, flexShrink: 0, borderRadius: 3,
              border: `1.5px solid ${todo.is_done ? "#16A34A" : "var(--border)"}`, background: todo.is_done ? "#16A34A" : "transparent",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {todo.is_done && <I.Check size={10} style={{ color: "#fff" }} />}
          </button>
          {editing ? (
            <input autoFocus value={draftTitle} onChange={e => setDraftTitle(e.target.value)}
              onClick={e => e.stopPropagation()}
              onBlur={() => { setEditing(false); if (draftTitle.trim() && draftTitle !== todo.title) onRename(draftTitle); else setDraftTitle(todo.title); }}
              onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { setDraftTitle(todo.title); setEditing(false); } }}
              style={{ flex: 1, fontSize: 13, lineHeight: 1.45, border: "1px solid var(--p-400, #6366f1)", borderRadius: 4, padding: "1px 4px", outline: "none", fontFamily: "inherit", color: "var(--text-1)" }} />
          ) : (
            <span onClick={onOpen} onDoubleClick={e => { e.stopPropagation(); setDraftTitle(todo.title); setEditing(true); }}
              title="더블클릭하여 이름 수정"
              style={{ flex: 1, fontSize: 13, lineHeight: 1.45, textDecoration: todo.is_done ? "line-through" : "none",
              color: todo.is_done ? "var(--text-3)" : "var(--text-1)", wordBreak: "break-word", cursor: "pointer" }}>
              {todo.title}
            </span>
          )}
          {!isOwner && <span title="다른 사람이 할당한 항목" style={{ fontSize: 9, padding: "1px 4px", borderRadius: 3, background: "#6366f118", color: "#6366f1", fontWeight: 600, flexShrink: 0 }}>공유</span>}
        </div>
        {todo.note && <div style={{ marginTop: 4, fontSize: 11, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{todo.note}</div>}
        {hasCl && (
          <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ flex: 1, height: 4, borderRadius: 2, background: "var(--border)" }}>
              <div style={{ width: `${Math.round((cl.done / cl.total) * 100)}%`, height: "100%", borderRadius: 2, background: cl.done === cl.total ? "#22C55E" : "var(--p-500, #6366f1)", transition: "width .2s" }} />
            </div>
            <span style={{ fontSize: 9, color: "var(--text-3)", flexShrink: 0 }}>{cl.done}/{cl.total}</span>
          </div>
        )}
        <div style={{ display: "flex", gap: 6, marginTop: 7, alignItems: "center", flexWrap: "wrap" }}>
          <span title="진행상황" style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 3, background: `${scol.color}18`, color: scol.color, display: "inline-flex", alignItems: "center", gap: 3 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: scol.color }} />{scol.label}
          </span>
          {todo.category && (
            <span title="카테고리" style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 3, background: "var(--bg-2,#eef2ff)", color: "var(--text-2)" }}>
              {todo.category}
            </span>
          )}
          <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: `${pColor}20`, color: pColor }}>
            {PRIORITY_LABEL[todo.priority.toUpperCase()] ?? todo.priority}
          </span>
          {(todo.start_date || todo.due_date) && (
            <span style={{ fontSize: 10, color: todo.due_date && daysUntil(todo.due_date) < 0 && !todo.is_done ? "#EF4444" : "var(--text-3)" }}>
              📅 {todo.start_date ? todo.start_date.slice(5) + (todo.start_time ? " " + todo.start_time : "") : ""}{todo.start_date && todo.due_date ? " ~ " : ""}{todo.due_date ? todo.due_date.slice(5) + (todo.due_time ? " " + todo.due_time : "") : ""}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <AvatarStack ids={todo.assignee_ids} users={users} />
        </div>
      </div>
    </div>
  );
}

// ── Kanban Column ─────────────────────────────────────────────────────────────

function KanbanCol({ col, todos, users, allLabels, currentUserId, onToggle, onCtxCard, onCtxCol, onOpen, onRename, onQuickAdd, onCardDrop, onInboxDrop, onLaneReorder, canReorder, selectedIds,
  dragOverCol, setDragOverCol, draggingId, setDraggingId, addingCol, setAddingCol }: {
  col: { key: string; label: string; color: string; hint: string }; todos: Todo[]; users: DirUser[]; allLabels: LabelInfo[]; currentUserId: number;
  onToggle: (id: number) => void;
  onCtxCard: (e: React.MouseEvent, todo: Todo, colKey: string) => void;
  onCtxCol: (e: React.MouseEvent, colKey: string) => void;
  onOpen: (id: number) => void; onRename: (id: number, title: string) => void; onQuickAdd: (colKey: string, title: string) => void;
  onCardDrop: (draggingId: number | null, colKey: string) => void;
  onInboxDrop: (todoId: number, colKey: string) => void;
  onLaneReorder: (srcKey: string, destKey: string) => void;
  canReorder: boolean;
  selectedIds: Set<number>;
  dragOverCol: string | null; setDragOverCol: (id: string | null) => void;
  draggingId: number | null; setDraggingId: (id: number | null) => void;
  addingCol: string | null; setAddingCol: (id: string | null) => void;
}) {
  const [quickText, setQuickText] = useState("");
  const quickRef = useRef<HTMLInputElement>(null);
  const isOver = dragOverCol === col.key;
  // 헤더 + 버튼 / 컨텍스트 메뉴 '새 할 일' → 하단 상시 입력창으로 포커스
  useEffect(() => { if (addingCol === col.key) { quickRef.current?.focus(); setAddingCol(null); } }, [addingCol, col.key, setAddingCol]);
  const submitQuick = () => { const v = quickText.trim(); if (!v) return; onQuickAdd(col.key, v); setQuickText(""); /* 입력 유지 → 바로 또 추가 */ };

  return (
    <div onDragOver={e => { e.preventDefault(); setDragOverCol(col.key); }} onDragLeave={() => setDragOverCol(null)}
      onDrop={e => { e.preventDefault();
        const lane = e.dataTransfer.getData("application/x-lane-key");
        if (lane) { if (lane !== col.key) onLaneReorder(lane, col.key); }
        else if (draggingId != null) { onCardDrop(draggingId, col.key); }
        else { const raw = e.dataTransfer.getData("application/x-todo-id"); if (raw) onInboxDrop(Number(raw), col.key); }
        setDragOverCol(null); }}
      onContextMenu={e => { e.preventDefault(); e.stopPropagation(); onCtxCol(e, col.key); }}
      style={{ flex: "0 0 248px", alignSelf: "flex-start", display: "flex", flexDirection: "column", borderRadius: 8,
        background: isOver ? `${col.color}0A` : "linear-gradient(180deg, var(--bg-1,#f8f9fa), var(--bg-0,#fff))", border: `1.5px solid ${isOver ? col.color : "var(--border)"}`,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), 0 1px 3px rgba(15,23,42,0.05)",
        transition: "border-color .15s, background .15s", overflow: "hidden" }}>
      <div draggable={canReorder}
        onDragStart={canReorder ? (e => { e.dataTransfer.setData("application/x-lane-key", col.key); e.dataTransfer.effectAllowed = "move"; }) : undefined}
        title={canReorder ? "드래그하여 카드 리스트 순서 변경" : undefined}
        style={{ padding: "10px 12px 8px", borderBottom: `2px solid ${col.color}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, cursor: canReorder ? "grab" : "default" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.color, flexShrink: 0 }} />
          <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text-1)" }}>{col.label}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 8, background: `${col.color}20`, color: col.color }}>{todos.length}</span>
        </div>
        <button onClick={() => quickRef.current?.focus()} title={col.hint}
          style={{ width: 22, height: 22, borderRadius: 5, border: "none", background: `${col.color}18`, color: col.color, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <I.Plus size={13} />
        </button>
      </div>
      <div style={{ padding: "8px 8px 4px", display: "flex", flexDirection: "column", gap: 6 }}>
        {todos.map(t => (
          <TodoCard key={t.id} todo={t} users={users} allLabels={allLabels} currentUserId={currentUserId}
            onToggle={() => onToggle(t.id)} onCtx={e => { e.preventDefault(); onCtxCard(e, t, col.key); }} onOpen={() => onOpen(t.id)}
            onRename={(title) => onRename(t.id, title)} selected={selectedIds.has(t.id)}
            dragging={draggingId === t.id} onDragStart={() => setDraggingId(t.id)} onDragEnd={() => setDraggingId(null)} />
        ))}
      </div>
      {/* 하단 상시 입력 — 엔터로 추가하고 바로 또 추가 */}
      <div style={{ padding: "6px 8px 8px", borderTop: "1px solid var(--border)", flexShrink: 0 }}>
        <input ref={quickRef} value={quickText} onChange={e => setQuickText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") submitQuick(); if (e.key === "Escape") { setQuickText(""); (e.target as HTMLInputElement).blur(); } }}
          placeholder="＋ 할 일 추가 (Enter)"
          style={{ width: "100%", boxSizing: "border-box", fontSize: 12, padding: "7px 9px", border: "1px dashed var(--border)", borderRadius: 6, background: "var(--bg-0,#fff)", color: "var(--text-1)", outline: "none", fontFamily: "inherit" }} />
      </div>
    </div>
  );
}

// ── Table View ────────────────────────────────────────────────────────────────

function TableView({ todos, users, allLabels, pageById, onToggle, onOpen, onCtx }: {
  todos: Todo[]; users: DirUser[]; allLabels: LabelInfo[]; pageById: Map<number, PageInfo>;
  onToggle: (id: number) => void; onOpen: (id: number) => void; onCtx: (e: React.MouseEvent, t: Todo) => void;
}) {
  const th: React.CSSProperties = { textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: ".04em", borderBottom: "1px solid var(--border)", position: "sticky", top: 0, background: "var(--bg-0)" };
  const td: React.CSSProperties = { padding: "8px 12px", fontSize: 13, borderBottom: "1px solid var(--border)", verticalAlign: "middle" };
  return (
    <div style={{ flex: 1, overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 34 }}></th>
            <th style={th}>제목</th>
            <th style={{ ...th, width: 110 }}>페이지</th>
            <th style={{ ...th, width: 88 }}>상태</th>
            <th style={{ ...th, width: 64 }}>우선순위</th>
            <th style={{ ...th, width: 104 }}>마감</th>
            <th style={{ ...th, width: 96 }}>담당자</th>
          </tr>
        </thead>
        <tbody>
          {todos.length === 0 && (
            <tr><td colSpan={7} style={{ ...td, textAlign: "center", color: "var(--text-3)", padding: 32 }}>표시할 할 일이 없습니다.</td></tr>
          )}
          {todos.map(t => {
            const col = COLS.find(c => c.id === mapStatus(t))!;
            const pColor = PRIORITY_COLOR[t.priority.toUpperCase()] ?? "#94A3B8";
            const page = t.page_id ? pageById.get(t.page_id) : null;
            const overdue = t.due_date && daysUntil(t.due_date) < 0 && !t.is_done;
            return (
              <tr key={t.id} onContextMenu={e => onCtx(e, t)}
                style={{ cursor: "pointer", transition: "background .1s" }}
                onMouseEnter={e => (e.currentTarget.style.background = "var(--hover, #f7f7f8)")}
                onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                <td style={td}>
                  <button onClick={e => { e.stopPropagation(); onToggle(t.id); }}
                    style={{ width: 16, height: 16, borderRadius: 3, border: `1.5px solid ${t.is_done ? "#16A34A" : "var(--border)"}`, background: t.is_done ? "#16A34A" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {t.is_done && <I.Check size={10} style={{ color: "#fff" }} />}
                  </button>
                </td>
                <td style={td} onClick={() => onOpen(t.id)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {(allLabels.filter(l => t.label_ids?.includes(l.id))).slice(0, 3).map(lb => <span key={lb.id} style={{ width: 8, height: 8, borderRadius: 2, background: lb.color, flexShrink: 0 }} title={lb.name} />)}
                    <span style={{ textDecoration: t.is_done ? "line-through" : "none", color: t.is_done ? "var(--text-3)" : "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</span>
                  </div>
                </td>
                <td style={{ ...td, color: "var(--text-3)" }} onClick={() => onOpen(t.id)}>
                  {page ? <span>{page.icon && page.icon !== "FileText" ? page.icon : "📄"} {page.title}</span> : <span style={{ opacity: 0.5 }}>—</span>}
                </td>
                <td style={td} onClick={() => onOpen(t.id)}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 7px", borderRadius: 10, background: `${col.color}18`, color: col.color }}>{col.label}</span>
                </td>
                <td style={td} onClick={() => onOpen(t.id)}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: pColor }}>{PRIORITY_LABEL[t.priority.toUpperCase()] ?? t.priority}</span>
                </td>
                <td style={{ ...td, color: overdue ? "#EF4444" : "var(--text-2)", fontWeight: overdue ? 600 : 400 }} onClick={() => onOpen(t.id)}>
                  {t.due_date ?? <span style={{ opacity: 0.4 }}>—</span>}
                </td>
                <td style={td} onClick={() => onOpen(t.id)}><AvatarStack ids={t.assignee_ids} users={users} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Calendar View ─────────────────────────────────────────────────────────────

function CalendarView({ todos, onOpen }: { todos: Todo[]; onOpen: (id: number) => void }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const todayStr = useMemo(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }, []);

  const byDate = useMemo(() => {
    const map = new Map<string, Todo[]>();
    const add = (key: string, t: Todo) => { const a = map.get(key) ?? []; a.push(t); map.set(key, a); };
    const key = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    todos.forEach(t => {
      const s = t.start_date, e = t.due_date;
      if (s && e) {
        const sd = new Date(s + "T00:00:00"), ed = new Date(e + "T00:00:00");
        let n = 0;
        for (let d = new Date(sd); d <= ed && n < 90; d.setDate(d.getDate() + 1), n++) add(key(d), t);
      } else if (e) add(e, t);
      else if (s) add(s, t);
    });
    return map;
  }, [todos]);

  const first = new Date(cursor.y, cursor.m, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const fmt = (d: number) => `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const move = (delta: number) => setCursor(c => { const nm = c.m + delta; return { y: c.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 }; });

  const DOW = ["일", "월", "화", "수", "목", "금", "토"];

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", padding: "12px 16px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <button className="t-btn" onClick={() => move(-1)} style={{ padding: "4px 10px" }}>‹</button>
        <span style={{ fontWeight: 700, fontSize: 15, color: "var(--text-1)", minWidth: 110, textAlign: "center" }}>{cursor.y}년 {cursor.m + 1}월</span>
        <button className="t-btn" onClick={() => move(1)} style={{ padding: "4px 10px" }}>›</button>
        <button className="t-btn" onClick={() => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); }} style={{ padding: "4px 12px", fontSize: 12 }}>오늘</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 1, marginBottom: 1 }}>
        {DOW.map((d, i) => (
          <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, padding: "4px 0", color: i === 0 ? "#EF4444" : i === 6 ? "#3B82F6" : "var(--text-3)" }}>{d}</div>
        ))}
      </div>
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gridAutoRows: "1fr", gap: 1, background: "var(--border)", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
        {cells.map((d, i) => {
          const dateStr = d ? fmt(d) : "";
          const items = d ? (byDate.get(dateStr) ?? []) : [];
          const isToday = dateStr === todayStr;
          return (
            <div key={i} style={{ background: "var(--bg-0)", padding: "4px 5px", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", opacity: d ? 1 : 0.4 }}>
              {d && (
                <>
                  <div style={{ fontSize: 11, fontWeight: isToday ? 700 : 500, color: isToday ? "#fff" : (i % 7 === 0 ? "#EF4444" : i % 7 === 6 ? "#3B82F6" : "var(--text-2)"),
                    background: isToday ? "var(--p-500, #6366f1)" : "transparent", borderRadius: isToday ? 10 : 0, width: isToday ? 18 : "auto", height: isToday ? 18 : "auto",
                    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 2, alignSelf: "flex-start", padding: isToday ? 0 : "0 2px" }}>{d}</div>
                  <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                    {items.slice(0, 4).map(t => {
                      const pColor = PRIORITY_COLOR[t.priority.toUpperCase()] ?? "#94A3B8";
                      return (
                        <button key={t.id} onClick={() => onOpen(t.id)} title={t.title}
                          style={{ display: "flex", alignItems: "center", gap: 3, padding: "1px 4px", borderRadius: 3, border: "none", cursor: "pointer",
                            background: `${pColor}1A`, color: t.is_done ? "var(--text-3)" : "var(--text-1)", textAlign: "left", width: "100%" }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: pColor, flexShrink: 0 }} />
                          <span style={{ fontSize: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: t.is_done ? "line-through" : "none" }}>
                            {(t.start_time || t.due_time) && <b style={{ fontWeight: 700 }}>{(t.start_date === dateStr ? t.start_time : "") || t.due_time || t.start_time} </b>}{t.title}
                          </span>
                        </button>
                      );
                    })}
                    {items.length > 4 && <span style={{ fontSize: 9, color: "var(--text-3)", paddingLeft: 4 }}>+{items.length - 4}개</span>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Inbox panel (우측, 폴더 무관 개인 카드) ──────────────────────────────────

function InboxPanel({ users, currentPageId, currentPageName, onOpenCard }: {
  users: DirUser[]; currentPageId: number | null; currentPageName: string | null; onOpenCard: (id: number) => void;
}) {
  const [open, setOpen] = useState(() => localStorage.getItem("todos-inbox-open") !== "false");
  const [cards, setCards] = useState<Todo[]>([]);
  const [input, setInput] = useState("");
  const taRef = useRef<HTMLTextAreaElement>(null);

  const load = () => api.get<Todo[]>("/todos?inbox=true").then(r => setCards(r.data ?? [])).catch(() => {});
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("todos-inbox-refresh", h);
    return () => window.removeEventListener("todos-inbox-refresh", h);
  }, []);

  const toggle = () => setOpen(o => { localStorage.setItem("todos-inbox-open", String(!o)); return !o; });

  const add = async () => {
    const t = input.trim(); if (!t) return;
    try { await api.post("/todos", { title: t, page_id: null }); setInput(""); load(); setTimeout(() => taRef.current?.focus(), 0); }
    catch (e: any) { toast.error("추가 실패", e?.response?.data?.error?.message); }
  };
  const moveToBoard = async (id: number) => {
    if (currentPageId == null) { toast.info("보드를 먼저 선택하세요", "왼쪽 폴더를 선택하면 그 보드로 보냅니다"); return; }
    try { await api.patch(`/todos/${id}`, { page_id: currentPageId }); load(); window.dispatchEvent(new Event("todos-board-refresh")); toast.success(`${currentPageName ?? "보드"}로 이동`); }
    catch { toast.error("이동 실패"); }
  };
  const del = async (id: number) => {
    try { await api.delete(`/todos/${id}`); load(); } catch { toast.error("삭제 실패"); }
  };

  if (!open) {
    return (
      <button onClick={toggle} title="인박스 열기"
        style={{ width: 40, flexShrink: 0, borderLeft: "1px solid var(--border)", background: "var(--bg-1, #f8f9fa)", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, paddingTop: 14, color: "var(--text-3)", border: "none", borderLeftWidth: 1, borderLeftStyle: "solid" }}>
        <I.Inbox size={18} />
        {cards.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: "var(--p-500)", color: "#fff", borderRadius: 8, padding: "1px 5px" }}>{cards.length}</span>}
        <span style={{ writingMode: "vertical-rl", fontSize: 11, fontWeight: 600, marginTop: 4 }}>인박스</span>
      </button>
    );
  }

  const onInboxDrop = (e: React.DragEvent) => {
    const raw = e.dataTransfer.getData("application/x-todo-id");
    if (!raw) return; e.preventDefault();
    api.patch(`/todos/${raw}`, { page_id: null })
      .then(() => { load(); window.dispatchEvent(new Event("todos-board-refresh")); toast.success("인박스로 이동"); })
      .catch(() => toast.error("이동 실패"));
  };
  return (
    <div onDragOver={e => { if (e.dataTransfer.types.includes("application/x-todo-id")) e.preventDefault(); }} onDrop={onInboxDrop}
      style={{ width: 280, flexShrink: 0, borderLeft: "1px solid var(--border)", background: "var(--bg-0, #fff)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <button onClick={toggle} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "12px 14px", borderBottom: "1px solid var(--border)", background: "none", border: "none", cursor: "pointer", width: "100%" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <I.Inbox size={15} style={{ color: "var(--p-500)" }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text-1)" }}>인박스</span>
          {cards.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: "var(--p-50,#eef2ff)", color: "var(--p-600)", borderRadius: 8, padding: "1px 6px" }}>{cards.length}</span>}
        </span>
        <I.ChevR size={14} style={{ color: "var(--text-3)" }} />
      </button>

      <div style={{ flex: 1, overflowY: "auto", padding: 8, display: "flex", flexDirection: "column", gap: 6 }}>
        {cards.length === 0 && (
          <div style={{ textAlign: "center", color: "var(--text-3)", fontSize: 12, padding: "30px 10px", lineHeight: 1.7 }}>
            폴더에 상관없이<br />빠르게 모아두는 공간이에요.<br />아래에서 추가하세요.
          </div>
        )}
        {cards.map(c => (
          <div key={c.id} className="t-card" draggable
            onDragStart={e => { e.dataTransfer.setData("application/x-todo-id", String(c.id)); e.dataTransfer.effectAllowed = "move"; }}
            title="폴더로 드래그하여 이동"
            style={{ padding: "9px 11px", cursor: "grab" }} onClick={() => onOpenCard(c.id)}>
            <div style={{ fontSize: 13, color: "var(--text-1)", lineHeight: 1.4, wordBreak: "break-word" }}>{c.title}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 7 }}>
              <button onClick={e => { e.stopPropagation(); moveToBoard(c.id); }} title={currentPageName ? `${currentPageName}로 이동` : "보드로 이동"}
                style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 5, border: "1px solid var(--border)", background: "var(--bg-1)", color: "var(--p-600)", cursor: "pointer" }}>→ 보드로</button>
              {c.assignee_ids.length > 0 && <AvatarStack ids={c.assignee_ids} users={users} size={16} />}
              <div style={{ flex: 1 }} />
              <button onClick={e => { e.stopPropagation(); del(c.id); }} title="삭제" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 12 }}>🗑</button>
            </div>
          </div>
        ))}
      </div>

      {/* 하단 입력 — 우하단 외관설정 FAB와 겹치지 않도록 추가 버튼은 왼쪽, 오른쪽 여백 확보 */}
      <div style={{ borderTop: "1px solid var(--border)", padding: "8px 8px 60px" }}>
        <textarea ref={taRef} value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); add(); } }}
          placeholder="카드 제목… (Enter로 추가)" rows={2}
          style={{ width: "100%", boxSizing: "border-box", fontSize: 13, padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg-1)", color: "var(--text-1)", resize: "none", fontFamily: "inherit" }} />
        <div style={{ display: "flex", justifyContent: "flex-start", marginTop: 6 }}>
          <button className="t-btn t-btn-primary" onClick={add} disabled={!input.trim()} style={{ fontSize: 12 }}>추가</button>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

function DashboardView({ todos, users, currentUserId, pageById, onOpen }: {
  todos: Todo[]; users: DirUser[]; currentUserId: number; pageById: Map<number, PageInfo>; onOpen: (id: number) => void;
}) {
  const open = todos.filter(t => !t.is_done);
  const mine = open.filter(t => t.user_id === currentUserId || t.assignee_ids.includes(currentUserId));
  const urgent = open.filter(t => ["URGENT", "HIGH"].includes(t.priority.toUpperCase()));
  const dueSoon = open.filter(t => t.due_date && daysUntil(t.due_date) <= 3).sort((a, b) => daysUntil(a.due_date!) - daysUntil(b.due_date!));
  const byStatus = COLS.map(c => ({ col: c, n: open.filter(t => mapStatus(t) === c.id).length }));

  const Row = ({ t }: { t: Todo }) => {
    const pColor = PRIORITY_COLOR[t.priority.toUpperCase()] ?? "#94A3B8";
    const page = t.page_id ? pageById.get(t.page_id) : null;
    const overdue = t.due_date && daysUntil(t.due_date) < 0;
    return (
      <div onClick={() => onOpen(t.id)} className="t-card" style={{ padding: "8px 11px", cursor: "pointer", display: "flex", flexDirection: "row", alignItems: "center", gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: pColor, flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title || "(제목 없음)"}</span>
        {t.category && <span style={{ fontSize: 10, color: "var(--text-3)", flexShrink: 0 }}>{t.category}</span>}
        {page && <span style={{ fontSize: 10, color: "var(--text-3)", flexShrink: 0 }}>📄 {page.title}</span>}
        {(t.start_date || t.due_date) && <span style={{ fontSize: 10, color: overdue ? "#EF4444" : "var(--text-3)", flexShrink: 0 }}>📅 {t.start_date ? t.start_date.slice(5) + (t.start_time ? " " + t.start_time : "") : ""}{t.start_date && t.due_date ? "~" : ""}{t.due_date ? t.due_date.slice(5) + (t.due_time ? " " + t.due_time : "") : ""}</span>}
        <AvatarStack ids={t.assignee_ids} users={users} size={16} />
      </div>
    );
  };
  const Card = ({ title, color, items, empty }: { title: string; color: string; items: Todo[]; empty: string }) => (
    <div style={{ flex: 1, minWidth: 280, display: "flex", flexDirection: "column", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", background: "var(--bg-0)" }}>
      <div style={{ padding: "10px 14px", borderBottom: `2px solid ${color}`, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text-1)" }}>{title}</span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 8, background: `${color}20`, color }}>{items.length}</span>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 10, display: "flex", flexDirection: "column", gap: 6, maxHeight: 360 }}>
        {items.length === 0 ? <div style={{ color: "var(--text-3)", fontSize: 12, padding: 16, textAlign: "center" }}>{empty}</div>
          : items.slice(0, 50).map(t => <Row key={t.id} t={t} />)}
      </div>
    </div>
  );

  return (
    <div style={{ flex: 1, overflow: "auto", padding: "16px 20px" }}>
      {/* 진행상황 집계 */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {byStatus.map(({ col, n }) => (
          <div key={col.id} style={{ flex: "1 1 120px", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 14px", background: "var(--bg-0)", borderTop: `3px solid ${col.color}` }}>
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>{col.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "var(--text-1)" }}>{n}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
        <Card title="🙋 내가 할당된 과업" color="#2563EB" items={mine} empty="할당된 과업이 없습니다." />
        <Card title="🔥 긴급 과업" color="#EF4444" items={urgent} empty="긴급 과업이 없습니다." />
        <Card title="⏰ 마감 임박 / 지남" color="#D97706" items={dueSoon} empty="임박한 마감이 없습니다." />
      </div>
    </div>
  );
}

// ── Archive (보관함) ──────────────────────────────────────────────────────────

function ArchiveView({ todos, users, pageById, onOpen, onRestore, onDelete }: {
  todos: Todo[]; users: DirUser[]; pageById: Map<number, PageInfo>;
  onOpen: (id: number) => void; onRestore: (t: Todo) => void; onDelete: (id: number) => void;
}) {
  return (
    <div style={{ flex: 1, overflow: "auto", padding: "16px 20px" }}>
      <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 10 }}>취소·보관된 카드 {todos.length}개 — 복원하면 다시 목록에 나타납니다.</div>
      {todos.length === 0 ? (
        <div style={{ color: "var(--text-3)", fontSize: 13, padding: 40, textAlign: "center" }}>보관된 카드가 없습니다.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: 760 }}>
          {todos.map(t => {
            const col = COLS.find(c => c.id === (t.status as ColStatus)) ?? COLS[0];
            const page = t.page_id ? pageById.get(t.page_id) : null;
            return (
              <div key={t.id} className="t-card" style={{ flexDirection: "row", padding: "8px 11px", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: col.color, flexShrink: 0 }} />
                <span onClick={() => onOpen(t.id)} style={{ flex: 1, minWidth: 0, fontSize: 13, color: "var(--text-1)", cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title || "(제목 없음)"}</span>
                <span style={{ fontSize: 10, color: "var(--text-3)", flexShrink: 0 }}>{col.label}</span>
                {t.category && <span style={{ fontSize: 10, color: "var(--text-3)", flexShrink: 0 }}>{t.category}</span>}
                {page && <span style={{ fontSize: 10, color: "var(--text-3)", flexShrink: 0 }}>📄 {page.title}</span>}
                <AvatarStack ids={t.assignee_ids} users={users} size={16} />
                <button onClick={() => onRestore(t)} className="t-btn" style={{ fontSize: 11, padding: "3px 9px", flexShrink: 0 }}>↩ 복원</button>
                <button onClick={() => onDelete(t.id)} style={{ border: "none", background: "none", cursor: "pointer", color: "#DC2626", fontSize: 13, flexShrink: 0 }} title="영구 삭제">🗑</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function TodosPage() {
  const { user: authUser } = useAuth();
  const currentUserId = authUser?.id ?? 0;
  const [params] = useSearchParams();
  const pageParam = params.get("page") || "all";
  const isArchive = pageParam === "archive";
  const currentPageId = (pageParam === "all" || isArchive) ? null : Number(pageParam);

  const [todos, setTodos] = useState<Todo[]>([]);
  const [users, setUsers] = useState<DirUser[]>([]);
  const [pages, setPages] = useState<PageInfo[]>([]);
  const [allLabels, setAllLabels] = useState<LabelInfo[]>([]);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState<ViewMode>(() => (localStorage.getItem("todos-view") as ViewMode) || "board");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTER);
  const [showFilter, setShowFilter] = useState(false);

  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [addingCol, setAddingCol] = useState<string | null>(null);
  const [ctxMenu, setCtxMenu] = useState<CtxState | null>(null);
  const [editModal, setEditModal] = useState<EditPayload | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [groupBy, setGroupBy] = useState<"status" | "category">(() => (localStorage.getItem("todos-groupby2") as "status" | "category") || "category");
  const setGroup = (g: "status" | "category") => { setGroupBy(g); localStorage.setItem("todos-groupby2", g); };
  // 폴더(페이지)별 카테고리 카드리스트 순서 + 빈 리스트 — 폴더마다 따로 저장
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [marquee, setMarquee] = useState<{ l: number; t: number; w: number; h: number } | null>(null);
  const catKey = `todos-cat-order2:${currentPageId ?? "all"}`;  // v2: 폴더별 분리(이전 키의 잘못 섞인 데이터 폐기)
  const [catOrder, setCatOrder] = useState<string[]>([]);
  useEffect(() => { try { setCatOrder(JSON.parse(localStorage.getItem(catKey) || "[]")); } catch { setCatOrder([]); } }, [catKey]);
  const saveCatOrder = (arr: string[]) => { setCatOrder(arr); localStorage.setItem(catKey, JSON.stringify(arr)); };

  const pageById = useMemo(() => new Map(pages.map(p => [p.id, p])), [pages]);
  const currentPage = currentPageId != null ? pageById.get(currentPageId) : null;

  const setViewMode = (v: ViewMode) => { setView(v); localStorage.setItem("todos-view", v); };

  // ── Data loading ──────────────────────────────────────────────────────────
  const reload = async () => {
    setLoading(true);
    try {
      const url = isArchive ? "/todos?archived=true" : currentPageId != null ? `/todos?page_id=${currentPageId}` : "/todos";
      const res = await api.get<Todo[]>(url);
      setTodos(res.data ?? []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [pageParam]);

  // 인박스에서 카드를 보드로 옮기면 현재 보드 갱신
  useEffect(() => {
    const h = () => reload();
    window.addEventListener("todos-board-refresh", h);
    return () => window.removeEventListener("todos-board-refresh", h);
    /* eslint-disable-next-line */
  }, [pageParam]);

  const loadPages = () => api.get<PageInfo[]>("/todos/pages").then(r => setPages(r.data ?? [])).catch(() => {});
  useEffect(() => {
    api.get<any>("/users?size=200").then(r => { const b: any = r.data; setUsers(Array.isArray(b) ? b : (b?.data ?? b?.items ?? [])); }).catch(() => {});
    api.get<LabelInfo[]>("/todos/labels").then(r => setAllLabels(r.data ?? [])).catch(() => {});
    loadPages();
    const h = () => loadPages();
    window.addEventListener("todos-pages-updated", h);
    return () => window.removeEventListener("todos-pages-updated", h);
  }, []);

  // ── Filtering ─────────────────────────────────────────────────────────────
  const visible = useMemo(() => todos.filter(t => {
    if (search) {
      const hay = `${t.title} ${t.note ?? ""}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    if (filters.priorities.length && !filters.priorities.includes(t.priority.toUpperCase())) return false;
    if (filters.labels.length && !filters.labels.some(id => t.label_ids?.includes(id))) return false;
    if (filters.mineOnly && !(t.user_id === currentUserId || t.assignee_ids.includes(currentUserId))) return false;
    if (filters.due !== "all") {
      if (filters.due === "none") { if (t.due_date) return false; }
      else {
        if (!t.due_date) return false;
        const diff = daysUntil(t.due_date);
        if (filters.due === "overdue" && diff >= 0) return false;
        if (filters.due === "soon" && (diff < 0 || diff > 7)) return false;
      }
    }
    return true;
  }), [todos, search, filters, currentUserId]);

  const activeFilterCount = filters.labels.length + filters.priorities.length + (filters.due !== "all" ? 1 : 0) + (filters.mineOnly ? 1 : 0);

  // 카테고리 목록 + 보드 레인(그룹 컬럼) — 진행상황 또는 카테고리 기준
  const CAT_PALETTE = ["#2563EB", "#D97706", "#7C3AED", "#16A34A", "#DC2626", "#0891B2", "#DB2777", "#0D9488", "#F97316", "#6366F1"];
  const categories = useMemo(() => {
    const s = new Set<string>();
    todos.forEach(t => { if ((t.category || "").trim()) s.add(t.category.trim()); });
    return Array.from(s).sort((a, b) => a.localeCompare(b, "ko"));
  }, [todos]);
  // 카드가 존재하는 카테고리는 catOrder에 영구 기록 → 카드가 모두 빠져도 빈 카드리스트가 사라지지 않음.
  // deps는 categories만 — catKey를 넣으면 페이지 전환 순간(todos 갱신 전) 이전 페이지 카테고리가 새 키에 잘못 기록됨.
  useEffect(() => {
    const missing = categories.filter(c => !catOrder.includes(c));
    if (missing.length) saveCatOrder([...catOrder, ...missing]);
    /* eslint-disable-next-line */
  }, [categories]);
  const lanes = useMemo(() => {
    if (groupBy === "status") return COLS.map(c => ({ key: c.id as string, label: c.label, color: c.color, hint: c.hint }));
    // 저장된 순서(빈 리스트 포함) + 카드에서 나온 카테고리 합치기
    const ordered: string[] = [];
    const seen = new Set<string>();
    [...catOrder, ...categories].forEach(c => { if (c && !seen.has(c)) { seen.add(c); ordered.push(c); } });
    const out = ordered.map((c, i) => ({ key: c, label: c, color: CAT_PALETTE[i % CAT_PALETTE.length], hint: `${c} 카테고리` }));
    out.push({ key: "", label: "미분류", color: "#94A3B8", hint: "카테고리 없음" });
    return out;
    /* eslint-disable-next-line */
  }, [groupBy, categories, catOrder]);
  const cardsInLane = (key: string) => groupBy === "status"
    ? visible.filter(t => mapStatus(t) === key)
    : visible.filter(t => (t.category || "") === key && mapStatus(t) !== "done");  // 카테고리별 보기에선 완료 카드 숨김

  // ── 실행취소(Undo) 스택 — 최대 20개 (Ctrl+Z) ────────────────────────────────
  const undoStack = useRef<(() => Promise<void>)[]>([]);
  const pushUndo = (fn: () => Promise<void>) => { undoStack.current.push(fn); if (undoStack.current.length > 20) undoStack.current.shift(); };
  // 변경 전 카드 스냅샷(현재 todos에서) → 필드 원복용
  const snapshot = (ids: number[]) => todos.filter(t => ids.includes(t.id)).map(t => ({ ...t }));
  const restoreFields = (s: Todo) => ({
    title: s.title, note: s.note, priority: s.priority, status: s.status, category: s.category,
    page_id: s.page_id, is_archived: s.is_archived, is_done: s.is_done, cover_color: s.cover_color,
    start_date: s.start_date, due_date: s.due_date, start_time: s.start_time, due_time: s.due_time, assignee_ids: s.assignee_ids,
  });
  const undoRestore = (snaps: Todo[]) => async () => {
    for (const s of snaps) { await api.patch(`/todos/${s.id}`, restoreFields(s)).catch(() => {}); }
    reload(); window.dispatchEvent(new Event("todos-inbox-refresh"));
  };
  const undoRecreate = (snaps: Todo[]) => async () => {
    for (const s of snaps) { await api.post(`/todos`, restoreFields(s)).catch(() => {}); }
    reload(); window.dispatchEvent(new Event("todos-inbox-refresh"));
  };
  const doUndo = async () => {
    const fn = undoStack.current.pop();
    if (!fn) { toast.info("되돌릴 작업이 없습니다"); return; }
    try { await fn(); toast.success("되돌렸습니다"); } catch { toast.error("되돌리기 실패"); }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z") && !e.shiftKey) {
        const tg = e.target as HTMLElement;
        if (tg && tg.closest("input, textarea, [contenteditable='true']")) return;  // 입력 중엔 무시
        if (detailId != null || editModal) return;  // 모달 열려 있으면 무시
        e.preventDefault(); doUndo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    /* eslint-disable-next-line */
  }, [detailId, editModal]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleToggle = async (id: number) => {
    const snaps = snapshot([id]);
    try { const res = await api.post<Todo>(`/todos/${id}/toggle`); setTodos(prev => prev.map(t => t.id === id ? res.data : t)); pushUndo(undoRestore(snaps)); }
    catch { toast.error("토글 실패"); }
  };
  const handleMove = async (id: number, to: ColStatus) => {
    const snaps = snapshot([id]);
    try { const res = await api.patch<Todo>(`/todos/${id}`, { status: to, is_done: to === "done" }); setTodos(prev => prev.map(t => t.id === id ? res.data : t).filter(t => !t.is_archived)); if (snaps.length) pushUndo(undoRestore(snaps)); }
    catch { toast.error("이동 실패"); }
  };
  const handleLaneAdd = async (laneKey: string, title: string) => {
    const body: any = { title, priority: "NORMAL", page_id: currentPageId };
    if (groupBy === "status") body.status = laneKey; else { body.category = laneKey; body.status = "inbox"; }
    try { const res = await api.post<Todo>("/todos", body); setTodos(prev => [...prev, res.data]); const nid = res.data.id; pushUndo(async () => { await api.delete(`/todos/${nid}`).catch(() => {}); reload(); window.dispatchEvent(new Event("todos-inbox-refresh")); }); }
    catch (e: any) { toast.error("추가 실패", e?.response?.data?.error?.message); }
  };
  const handleLaneDrop = (dragId: number | null, laneKey: string) => {
    if (dragId == null) return;
    const ids = (selectedIds.has(dragId) && selectedIds.size > 1) ? Array.from(selectedIds) : [dragId];
    if (groupBy === "status") ids.forEach(id => handleMove(id, laneKey as ColStatus));
    else ids.forEach(id => handleSetCategory(id, laneKey));
    if (ids.length > 1) setSelectedIds(new Set());
  };
  // 빈 보드에서 드래그 → 사각 영역 안의 카드 다중 선택(Shift로 추가 선택)
  // 빈 보드 영역 드래그: 그냥 끌면 좌우 이동(팬), Shift+끌면 다중 선택(마키)
  const onBoardMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const tgt = e.target as HTMLElement;
    if (tgt.closest("[data-card-id], button, input, textarea, select, a")) return;
    if (e.shiftKey) startMarquee(e); else startPan(e);
  };
  const startPan = (e: React.MouseEvent) => {
    const el = e.currentTarget as HTMLElement;
    const sx = e.clientX, sl = el.scrollLeft;
    let moved = false;
    el.style.cursor = "grabbing";
    const onMove = (ev: MouseEvent) => { moved = true; el.scrollLeft = sl - (ev.clientX - sx); };
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); el.style.cursor = ""; if (!moved) setSelectedIds(new Set()); };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
  };
  const startMarquee = (e: React.MouseEvent) => {
    const sx = e.clientX, sy = e.clientY;
    const base = new Set<number>();
    setSelectedIds(base);
    const onMove = (ev: MouseEvent) => {
      const l = Math.min(sx, ev.clientX), t = Math.min(sy, ev.clientY), r = Math.max(sx, ev.clientX), b = Math.max(sy, ev.clientY);
      setMarquee({ l, t, w: r - l, h: b - t });
      const sel = new Set<number>();
      document.querySelectorAll<HTMLElement>("[data-card-id]").forEach(el => {
        const rc = el.getBoundingClientRect();
        if (rc.left < r && rc.right > l && rc.top < b && rc.bottom > t) sel.add(Number(el.getAttribute("data-card-id")));
      });
      setSelectedIds(sel);
    };
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); setMarquee(null); };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
  };
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds); if (!ids.length) return;
    if (!confirm(`선택한 ${ids.length}개 카드를 삭제하시겠습니까?`)) return;
    const snaps = snapshot(ids);
    try { await Promise.all(ids.map(id => api.delete(`/todos/${id}`))); setTodos(prev => prev.filter(t => !selectedIds.has(t.id))); setSelectedIds(new Set()); pushUndo(undoRecreate(snaps)); toast.success(`${ids.length}개 삭제됨`); }
    catch { toast.error("삭제 실패"); }
  };
  // 카드 리스트(카테고리) 드래그 재정렬 — 카테고리 모드
  const handleLaneReorder = (src: string, dest: string) => {
    if (src === dest || groupBy !== "category") return;
    const order = lanes.filter(l => l.key !== "").map(l => l.key);
    const from = order.indexOf(src); if (from < 0) return;
    order.splice(from, 1);
    const to = order.indexOf(dest);
    order.splice(to < 0 ? order.length : to, 0, src);
    saveCatOrder(order);
  };
  // 인박스 카드를 현재 보드의 레인으로 이동(폴더 배정 + 상태/카테고리 지정)
  const handleInboxToLane = async (todoId: number, laneKey: string) => {
    const body: any = { page_id: currentPageId };
    if (groupBy === "status") body.status = laneKey; else body.category = laneKey;
    try {
      await api.patch(`/todos/${todoId}`, body); reload(); window.dispatchEvent(new Event("todos-inbox-refresh"));
      pushUndo(async () => { await api.patch(`/todos/${todoId}`, { page_id: null }).catch(() => {}); reload(); window.dispatchEvent(new Event("todos-inbox-refresh")); });
    }
    catch { toast.error("이동 실패"); }
  };
  const handleSetCategory = async (id: number, cat: string) => {
    const snaps = snapshot([id]);
    try { const res = await api.patch<Todo>(`/todos/${id}`, { category: cat }); setTodos(prev => prev.map(t => t.id === id ? res.data : t).filter(t => !t.is_archived)); if (snaps.length) pushUndo(undoRestore(snaps)); }
    catch { toast.error("카테고리 변경 실패"); }
  };
  const handleRename = async (id: number, title: string) => {
    const v = title.trim(); if (!v) return;
    const snaps = snapshot([id]);
    try { const res = await api.patch<Todo>(`/todos/${id}`, { title: v }); setTodos(prev => prev.map(t => t.id === id ? res.data : t)); if (snaps.length) pushUndo(undoRestore(snaps)); }
    catch { toast.error("이름 변경 실패"); }
  };
  // ── 카드 리스트(카테고리) 관리 — 카테고리별 보기 ──
  const addCatList = () => {
    const name = prompt("카드 리스트(카테고리) 이름")?.trim();
    if (!name) return;
    if (!catOrder.includes(name) && !categories.includes(name)) saveCatOrder([...catOrder, name]);
    setGroup("category");
  };
  const renameCatList = async (oldName: string) => {
    const nn = prompt("카드 리스트 새 이름", oldName)?.trim();
    if (!nn || nn === oldName) return;
    try {
      await Promise.all(todos.filter(t => (t.category || "") === oldName).map(t => api.patch(`/todos/${t.id}`, { category: nn })));
      saveCatOrder(catOrder.map(c => c === oldName ? nn : c));
      reload();
    } catch { toast.error("이름 변경 실패"); }
  };
  const deleteCatList = async (name: string) => {
    if (!confirm(`'${name}' 카드 리스트를 삭제할까요?\n(과업은 삭제되지 않고 '미분류'로 이동합니다)`)) return;
    try {
      await Promise.all(todos.filter(t => (t.category || "") === name).map(t => api.patch(`/todos/${t.id}`, { category: "" })));
      saveCatOrder(catOrder.filter(c => c !== name));
      reload();
    } catch { toast.error("삭제 실패"); }
  };
  const moveCatList = (name: string, dir: -1 | 1) => {
    const ordered = lanes.filter(l => l.key !== "").map(l => l.key);
    const i = ordered.indexOf(name), j = i + dir;
    if (i < 0 || j < 0 || j >= ordered.length) return;
    [ordered[i], ordered[j]] = [ordered[j], ordered[i]];
    saveCatOrder(ordered);
  };
  const handleSave = async (form: EditPayload) => {
    if (form.id) {
      const res = await api.patch<Todo>(`/todos/${form.id}`, {
        title: form.title, note: form.note || null, priority: form.priority,
        start_date: form.start_date || null, due_date: form.due_date || null, start_time: form.start_time || null, due_time: form.due_time || null, status: form.status, category: form.category, assignee_ids: form.assignee_ids, page_id: form.page_id,
      });
      // If moved off the current page view, drop it from the list.
      setTodos(prev => {
        const next = prev.map(t => t.id === form.id ? res.data : t).filter(t => !t.is_archived);
        return currentPageId != null ? next.filter(t => t.page_id === currentPageId) : next;
      });
    } else {
      const res = await api.post<Todo>("/todos", {
        title: form.title, note: form.note || null, priority: form.priority,
        start_date: form.start_date || null, due_date: form.due_date || null, start_time: form.start_time || null, due_time: form.due_time || null, status: form.status, category: form.category, assignee_ids: form.assignee_ids, page_id: form.page_id,
      });
      if (currentPageId == null || res.data.page_id === currentPageId) setTodos(prev => [...prev, res.data]);
      else toast.success("추가됨", "다른 페이지에 저장되었습니다");
    }
  };
  const handleDelete = async (id: number) => {
    if (!confirm("이 할 일을 삭제하시겠습니까?")) return;
    const snaps = snapshot([id]);
    try { await api.delete(`/todos/${id}`); setTodos(prev => prev.filter(t => t.id !== id)); if (snaps.length) pushUndo(undoRecreate(snaps)); toast.success("삭제됨"); }
    catch (e: any) { toast.error("삭제 실패", e?.response?.data?.error?.message); }
  };
  const handleCopy = async (id: number) => {
    try { const res = await api.post<Todo>(`/todos/${id}/copy`); if (currentPageId == null) setTodos(prev => [...prev, res.data]); else toast.success("복사됨", "전체에서 확인하세요"); }
    catch { toast.error("복사 실패"); }
  };
  const handleArchive = async (id: number, archive: boolean) => {
    const snaps = snapshot([id]);
    try { await api.patch<Todo>(`/todos/${id}`, { is_archived: archive }); setTodos(prev => prev.filter(t => t.id !== id)); if (snaps.length) pushUndo(undoRestore(snaps)); toast.success(archive ? "보관됨" : "보관 해제됨"); }
    catch { toast.error("보관 실패"); }
  };
  // 보관함에서 복원(보관 해제). 취소된 카드는 진행상황을 수신함으로 되돌림.
  const handleRestore = async (t: Todo) => {
    const snaps = snapshot([t.id]);
    try {
      const body: any = { is_archived: false };
      if (t.status === "cancel") body.status = "inbox";
      await api.patch<Todo>(`/todos/${t.id}`, body);
      setTodos(prev => prev.filter(x => x.id !== t.id));
      window.dispatchEvent(new Event("todos-inbox-refresh"));
      if (snaps.length) pushUndo(undoRestore(snaps));
      toast.success("복원됨");
    } catch { toast.error("복원 실패"); }
  };
  const handleMoveToPage = async (id: number, pid: number | null) => {
    const snaps = snapshot([id]);
    try {
      await api.patch<Todo>(`/todos/${id}`, { page_id: pid });
      setTodos(prev => currentPageId != null ? prev.filter(t => t.id !== id) : prev.map(t => t.id === id ? { ...t, page_id: pid } : t));
      window.dispatchEvent(new Event("todos-inbox-refresh"));  // 전체(page 없음)로 옮기면 인박스에 반영
      if (snaps.length) pushUndo(undoRestore(snaps));
      toast.success(pid == null ? "전체로 이동" : `${pageById.get(pid)?.title ?? "페이지"}(으)로 이동`);
    } catch { toast.error("페이지 이동 실패"); }
  };
  const handleDetailChange = (partial: Partial<Todo> & { id: number }) => {
    setTodos(prev => prev.map(t => t.id === partial.id ? { ...t, ...partial } : t).filter(t => !t.is_archived));
    window.dispatchEvent(new Event("todos-inbox-refresh"));  // 인박스 카드 편집 반영
  };

  // ── Context menu ──────────────────────────────────────────────────────────
  const openCardCtx = (e: React.MouseEvent, todo: Todo, col: string) => { e.preventDefault(); e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY, todo, col }); };
  const openColCtx = (e: React.MouseEvent, col: string) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, todo: null, col }); };

  const buildCtxItems = (ctx: CtxState): CMenuItem[] => {
    const { todo, col } = ctx;
    if (!todo) {
      const inCat = groupBy === "category";
      if (inCat) {
        // 카테고리별 보기: 카드 리스트(카테고리) 관리
        const isBoard = col === "__board__";
        const manageable = !isBoard && col !== "";  // 미분류/빈칸은 추가만
        return [
          { label: "카드 리스트 추가", icon: "➕", onClick: addCatList },
          ...(manageable ? [
            { sep: true } as CMenuItem,
            { label: "이름 변경", icon: "✏️", onClick: () => renameCatList(col) },
            { label: "⬅️ 왼쪽으로 이동", onClick: () => moveCatList(col, -1) },
            { label: "➡️ 오른쪽으로 이동", onClick: () => moveCatList(col, 1) },
            { sep: true } as CMenuItem,
            { label: "삭제", icon: "🗑️", danger: true, onClick: () => deleteCatList(col) },
          ] : []),
          ...(!isBoard ? [{ sep: true } as CMenuItem, { label: "이 리스트에 새 할 일", icon: "📝", onClick: () => setAddingCol(col) }] : []),
        ];
      }
      return [
        { label: "새 할 일 추가", icon: "➕", onClick: () => setAddingCol(col) },
        { sep: true },
        { label: "편집 모달로 추가", icon: "✏️", onClick: () => setEditModal({ title: "", note: "", priority: "NORMAL", start_date: "", due_date: "", start_time: "", due_time: "",
          status: (col as ColStatus), category: "", assignee_ids: [], page_id: currentPageId }) },
      ];
    }
    const isOwner = todo.user_id === currentUserId;
    const otherCols = COLS.filter(c => c.id !== mapStatus(todo));
    const pageChildren: CMenuItem[] = [
      { label: "전체 (페이지 없음)", disabled: todo.page_id == null, onClick: () => handleMoveToPage(todo.id, null) },
      ...pages.map(p => ({ label: `${p.parent_id ? "└ " : ""}${p.title}`, disabled: todo.page_id === p.id, onClick: () => handleMoveToPage(todo.id, p.id) })),
    ];
    return [
      { label: "상세 보기", icon: "🔍", onClick: () => setDetailId(todo.id) },
      { label: "수정", icon: "✏️", disabled: !isOwner, onClick: () => setEditModal({
        id: todo.id, title: todo.title, note: todo.note ?? "", priority: todo.priority.toUpperCase(),
        start_date: todo.start_date ?? "", due_date: todo.due_date ?? "", start_time: todo.start_time ?? "", due_time: todo.due_time ?? "", status: mapStatus(todo), category: todo.category ?? "", assignee_ids: todo.assignee_ids, page_id: todo.page_id,
      }) },
      { label: "복사", icon: "📋", onClick: () => handleCopy(todo.id) },
      { sep: true },
      { label: "진행상황 변경", icon: "🔁", children: otherCols.map(c => ({ label: c.label, onClick: () => handleMove(todo.id, c.id) })) },
      { label: "폴더 이동", icon: "📁", children: pageChildren },
      { sep: true },
      { label: todo.is_archived ? "📦 보관 해제" : "📦 보관", disabled: !isOwner, onClick: () => handleArchive(todo.id, !todo.is_archived) },
      { label: "삭제", icon: "🗑️", danger: true, disabled: !isOwner, onClick: () => handleDelete(todo.id) },
    ];
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const VIEW_TABS: { id: ViewMode; label: string; icon: string }[] = [
    { id: "board", label: "보드", icon: "▦" },
    { id: "table", label: "테이블", icon: "☰" },
    { id: "calendar", label: "캘린더", icon: "▤" },
  ];

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 16px", height: 52, flexShrink: 0, borderBottom: "1px solid var(--border)", background: "var(--bg-0, #fff)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 18 }}>{isArchive ? "🗃️" : currentPage ? (currentPage.icon && currentPage.icon !== "FileText" ? currentPage.icon : "📄") : "🏠"}</span>
          <span style={{ fontWeight: 700, fontSize: 15, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {isArchive ? "보관함" : currentPage ? currentPage.title : "대시보드"}
          </span>
          {!loading && <span style={{ fontSize: 12, color: "var(--text-3)", flexShrink: 0 }}>{(isArchive ? todos.length : visible.length)}개</span>}
        </div>

        <div style={{ flex: 1 }} />

        {/* Search */}
        <div style={{ position: "relative", width: 200 }}>
          <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "var(--text-3)", pointerEvents: "none" }}><I.Search size={14} /></span>
          <input className="t-input" placeholder="검색..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: "100%", paddingLeft: 30, fontSize: 13, boxSizing: "border-box" }} />
        </div>

        {/* View toggle (대시보드에서는 숨김) */}
        {currentPageId != null && <div style={{ display: "flex", gap: 2, background: "var(--bg-1, #f1f1f3)", borderRadius: 8, padding: 2 }}>
          {VIEW_TABS.map(v => (
            <button key={v.id} onClick={() => setViewMode(v.id)} title={v.label}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 11px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600,
                background: view === v.id ? "var(--bg-0, #fff)" : "transparent", color: view === v.id ? "var(--p-600, #4f46e5)" : "var(--text-3)",
                boxShadow: view === v.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
              <span style={{ fontSize: 13 }}>{v.icon}</span>{v.label}
            </button>
          ))}
        </div>}

        {/* Group-by toggle (board only) */}
        {currentPageId != null && view === "board" && (
          <div style={{ display: "flex", gap: 2, background: "var(--bg-1, #f1f1f3)", borderRadius: 8, padding: 2 }} title="보드 그룹 기준">
            {([["status", "진행상황별"], ["category", "카테고리별"]] as const).map(([g, lbl]) => (
              <button key={g} onClick={() => setGroup(g)}
                style={{ padding: "5px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600,
                  background: groupBy === g ? "var(--bg-0, #fff)" : "transparent", color: groupBy === g ? "var(--p-600, #4f46e5)" : "var(--text-3)",
                  boxShadow: groupBy === g ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
                {lbl}
              </button>
            ))}
          </div>
        )}

        {/* Filter */}
        <div style={{ position: "relative" }}>
          <button onClick={() => setShowFilter(v => !v)}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 11px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
              border: `1.5px solid ${activeFilterCount ? "var(--p-400)" : "var(--border)"}`, background: activeFilterCount ? "var(--p-50, #eef2ff)" : "transparent", color: activeFilterCount ? "var(--p-600)" : "var(--text-2)" }}>
            <I.Filter size={14} /> 필터{activeFilterCount > 0 && <span style={{ background: "var(--p-500)", color: "#fff", borderRadius: 8, padding: "0 5px", fontSize: 10 }}>{activeFilterCount}</span>}
          </button>
          {showFilter && <FilterPanel filters={filters} allLabels={allLabels} onChange={setFilters} onClose={() => setShowFilter(false)} />}
        </div>

        <button className="t-btn t-btn-primary" onClick={() => setEditModal({ title: "", note: "", priority: "NORMAL", start_date: "", due_date: "", start_time: "", due_time: "", status: "inbox", category: "", assignee_ids: [], page_id: currentPageId })}>
          <I.Plus size={14} /> 새 할 일
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, color: "var(--text-3)", fontSize: 13 }}>불러오는 중…</div>
      ) : isArchive ? (
        <ArchiveView todos={todos} users={users} pageById={pageById} onOpen={id => setDetailId(id)} onRestore={handleRestore} onDelete={handleDelete} />
      ) : currentPageId == null ? (
        <DashboardView todos={todos} users={users} currentUserId={currentUserId} pageById={pageById} onOpen={id => setDetailId(id)} />
      ) : view === "board" ? (
        <div style={{ flex: 1, overflow: "auto", display: "flex", gap: 12, padding: "14px 16px", alignItems: "flex-start" }}
          onClick={() => setCtxMenu(null)}
          onMouseDown={onBoardMouseDown}
          onWheel={e => {
            const el = e.currentTarget;
            if (e.deltaY === 0 || e.shiftKey) return;
            // 세로 스크롤이 필요하면(컬럼이 길면) 기본 동작 유지, 아니면 휠을 좌우로
            if (el.scrollHeight > el.clientHeight + 2) return;
            if (el.scrollWidth > el.clientWidth) { el.scrollLeft += e.deltaY; e.preventDefault(); }
          }}
          onContextMenu={e => { if (groupBy === "category") { e.preventDefault(); openColCtx(e, "__board__"); } }}>
          {lanes.map(lane => (
            <KanbanCol key={lane.key || "__none__"} col={lane} todos={cardsInLane(lane.key)} users={users} allLabels={allLabels} currentUserId={currentUserId}
              onToggle={handleToggle} onCtxCard={openCardCtx} onCtxCol={openColCtx} onOpen={id => setDetailId(id)}
              onRename={handleRename} onQuickAdd={handleLaneAdd} onCardDrop={handleLaneDrop} onInboxDrop={handleInboxToLane} onLaneReorder={handleLaneReorder} canReorder={groupBy === "category"} selectedIds={selectedIds}
              dragOverCol={dragOverCol} setDragOverCol={setDragOverCol} draggingId={draggingId} setDraggingId={setDraggingId}
              addingCol={addingCol} setAddingCol={setAddingCol} />
          ))}
        </div>
      ) : view === "table" ? (
        <TableView todos={visible} users={users} allLabels={allLabels} pageById={pageById}
          onToggle={handleToggle} onOpen={id => setDetailId(id)} onCtx={(e, t) => openCardCtx(e, t, mapStatus(t))} />
      ) : (
        <CalendarView todos={visible} onOpen={id => setDetailId(id)} />
      )}
      </div>

      {/* 인박스 — 폴더 무관, 개인 종속 카드 */}
      <InboxPanel users={users} currentPageId={currentPageId} currentPageName={currentPage?.title ?? null} onOpenCard={id => setDetailId(id)} />

      {/* Context menu */}
      {ctxMenu && <ContextMenu x={ctxMenu.x} y={ctxMenu.y} items={buildCtxItems(ctxMenu)} onClose={() => setCtxMenu(null)} />}

      {/* Edit / Create modal */}
      {editModal && <EditModal initial={editModal} users={users} pages={pages} categories={categories} onClose={() => setEditModal(null)} onSave={handleSave} />}

      {/* Card detail */}
      {detailId != null && <CardDetail todoId={detailId} users={users} categories={Array.from(new Set([...catOrder, ...categories]))} onClose={() => setDetailId(null)} onChange={handleDetailChange} />}

      {/* 드래그 다중 선택 */}
      {marquee && <div style={{ position: "fixed", left: marquee.l, top: marquee.t, width: marquee.w, height: marquee.h, background: "rgba(99,102,241,0.12)", border: "1px solid var(--p-400,#818cf8)", borderRadius: 2, zIndex: 9998, pointerEvents: "none" }} />}
      {selectedIds.size > 0 && (
        <div style={{ position: "fixed", bottom: 18, left: "50%", transform: "translateX(-50%)", zIndex: 9997, background: "var(--text-1,#111827)", color: "#fff", padding: "8px 16px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, boxShadow: "0 6px 20px rgba(0,0,0,0.28)", display: "flex", gap: 10, alignItems: "center" }}>
          {selectedIds.size}개 선택됨 · 드래그로 이동
          <button onClick={handleBulkDelete} style={{ background: "#DC2626", border: "none", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "3px 12px", borderRadius: 999 }}>🗑 삭제</button>
          <button onClick={() => setSelectedIds(new Set())} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 999 }}>해제</button>
        </div>
      )}
    </div>
  );
}
